import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LogOut, LayoutDashboard, Globe, Menu, X, MessageCircle, Bell } from "lucide-react";
import api from "@/lib/api";
import PushToggleButton from "@/components/PushToggleButton";

export default function TopBar({ variant = "public" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  // Landing 2026 (creami palette): sulla home pubblica la topbar assume la
  // stessa tinta mauve del hero, con i pulsanti in cream. Sulle altre pagine
  // mantiene lo sfondo cream tradizionale.
  const onLandingHome = variant === "public" && location.pathname === "/";
  // v3 (2026-02-27) — badge separati: chat + notifiche indipendenti così
  // l'utente capisce a colpo d'occhio quale bucket ha elementi non letti.
  const [chatUnread, setChatUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const [markingRead, setMarkingRead] = useState(false);
  // Ref al fetcher corrente, così l'invalidazione lato UI (mark-as-read) può
  // sempre invocare l'ultima closure aggiornata anche quando l'effetto
  // non è ancora stato ricreato.
  const fetchUnreadRef = useRef(null);

  const toggleLang = () => {
    const newLang = i18n.language === "it" ? "en" : "it";
    i18n.changeLanguage(newLang);
    localStorage.setItem("ef_lang", newLang);
  };

  // Polling unread chat + notifiche
  //   • 8s quando la tab è visibile → sensazione "real-time"
  //   • pausato quando la tab è nascosta (mobile/pwa in background) → refresh
  //     immediato al ritorno via `visibilitychange`
  // + refresh immediato via CustomEvent `epicflow:unread-invalidate`
  //   (dispatchato quando l'utente legge una chat/notifica)
  // + refresh su cambio di route (`location.pathname`) → l'ingresso/uscita
  //   dal tab chat riflette subito il conteggio.
  // + service worker `message` (push) → invalidazione istantanea
  useEffect(() => {
    if (!user) { setChatUnread(0); setNotifUnread(0); fetchUnreadRef.current = null; return; }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const [chatR, notifR] = await Promise.allSettled([
          api.get("/chat/unread/count"),
          api.get("/notifications/unread-count"),
        ]);
        if (cancelled) return;
        const chatN = chatR.status === "fulfilled" ? (chatR.value.data?.unread || 0) : 0;
        const notifN = notifR.status === "fulfilled" ? (notifR.value.data?.unread || 0) : 0;
        setChatUnread(chatN);
        setNotifUnread(notifN);
      } catch { /* ignore */ }
    };
    fetchUnreadRef.current = fetchUnread;
    fetchUnread();
    // Polling condizionato alla visibilità
    let iv = null;
    const startPolling = () => {
      if (iv) return;
      iv = setInterval(fetchUnread, 8000);
    };
    const stopPolling = () => { if (iv) { clearInterval(iv); iv = null; } };
    if (document.visibilityState === "visible") startPolling();
    const onVis = () => {
      if (document.visibilityState === "visible") { fetchUnread(); startPolling(); }
      else stopPolling();
    };
    const onFocus = () => fetchUnread();
    const onInvalidate = () => fetchUnread();
    // Push message dal service worker → invalidazione istantanea
    const onSWMessage = (e) => {
      if (e.data?.type === "epicflow-push") fetchUnread();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    window.addEventListener("epicflow:unread-invalidate", onInvalidate);
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", onSWMessage);
    }
    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("epicflow:unread-invalidate", onInvalidate);
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", onSWMessage);
      }
      if (fetchUnreadRef.current === fetchUnread) fetchUnreadRef.current = null;
    };
  }, [user]);

  // Refetch al cambio di route: rende immediato il refresh quando l'utente
  // torna sulla dashboard dopo aver letto una chat, o cambia tab.
  useEffect(() => {
    if (fetchUnreadRef.current) fetchUnreadRef.current();
  }, [location.pathname, location.search]);


  const navLinks = variant === "public" ? [
    ["/#chisiamo", "Chi siamo"],
    ["/#corsi", t("nav.courses")],
    ["/#gravidanza", "Gravidanza"],
    ["/#sedi", t("nav.sedi")],
    ["/eventi", t("nav.events")],
    ["/blog", t("nav.blog")],
  ] : [];

  return (
    <>
      <header
        data-testid="top-bar"
        className={`sticky top-0 z-40 ${onLandingHome ? "bg-primary text-primary-foreground border-b border-primary-foreground/15" : "bg-background border-b border-border"}`}
      >
        {/* Riga 1 — logo + azioni account */}
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
            <img
              src="/assets/epicflow-logo.jpg"
              alt="Epic Flow Pilates"
              className={`w-12 h-12 rounded-full object-cover shadow-sm ring-1 ${onLandingHome ? "ring-primary-foreground/30" : "ring-primary/20"}`}
            />
            <div className="leading-none">
              <div className={`font-serif-display italic text-2xl ${onLandingHome ? "text-primary-foreground" : "text-primary"}`}>epic flow</div>
              <div className={`text-[10px] uppercase tracking-[0.32em] mt-1 ${onLandingHome ? "text-primary-foreground/70" : "text-muted-foreground"}`}>Pilates</div>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <button
              data-testid="lang-toggle"
              onClick={toggleLang}
              className={`hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-2 rounded-full ${onLandingHome ? "hover:bg-primary-foreground/10 text-primary-foreground" : "hover:bg-muted"}`}
            >
              <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />
              {i18n.language.toUpperCase()}
            </button>
            {user ? (
              <>
                <PushToggleButton compact />
                {/* Icona chat: SOLO chat unread. Click → tab chat del ruolo. */}
                <button
                  data-testid="chat-topbar-btn"
                  onClick={() => {
                    const role = user.role;
                    if (role === "client") nav("/area-cliente?tab=chat");
                    else if (role === "instructor") nav("/insegnante?tab=chat");
                    else nav("/admin?tab=chat");
                  }}
                  className="relative inline-flex items-center gap-2 text-sm p-2 rounded-full hover:bg-muted"
                  aria-label="Chat"
                  title={chatUnread > 0 ? `${chatUnread} chat non lette` : "Chat"}
                >
                  <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
                  {chatUnread > 0 && (
                    <span
                      data-testid="chat-unread-badge"
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-medium rounded-full flex items-center justify-center ring-2 ring-background"
                    >
                      {chatUnread > 99 ? "99+" : chatUnread}
                    </span>
                  )}
                </button>
                {/* Icona notifiche: SOLO notifiche unread. 
                    Click → tab notifiche del ruolo (per instructor: mark-all-read).
                    Shift+click → mark all read shortcut power-user. */}
                <button
                  data-testid="notif-topbar-btn"
                  onClick={async (e) => {
                    const doMarkAllRead = async (reason) => {
                      if (notifUnread === 0 || markingRead) return;
                      setMarkingRead(true);
                      try {
                        await api.post("/notifications/mark-all-read");
                        const n = notifUnread;
                        setNotifUnread(0);
                        window.dispatchEvent(new CustomEvent("epicflow:unread-invalidate"));
                        toast.success(`${n} notifica/e marcate come lette`);
                      } catch { toast.error("Errore nel marcare come lette"); }
                      finally { setMarkingRead(false); }
                    };
                    // Shift+click ovunque = mark all
                    if (e.shiftKey) { await doMarkAllRead("shift"); return; }
                    const role = user.role;
                    if (role === "client") nav("/area-cliente?tab=notifiche");
                    else if (role === "instructor") nav("/insegnante?tab=notifiche");
                    else nav("/admin?tab=notifiche");
                  }}
                  className="relative inline-flex items-center gap-2 text-sm p-2 rounded-full hover:bg-muted"
                  aria-label="Notifiche"
                  title={notifUnread > 0
                    ? `${notifUnread} notifiche non lette${" (Shift+click = marca tutte)"}`
                    : "Notifiche"}
                >
                  <Bell className="w-5 h-5" strokeWidth={1.5} />
                  {notifUnread > 0 && (
                    <span
                      data-testid="notif-unread-badge"
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-medium rounded-full flex items-center justify-center ring-2 ring-background"
                    >
                      {notifUnread > 99 ? "99+" : notifUnread}
                    </span>
                  )}
                </button>
                <button
                  data-testid="dashboard-btn"
                  onClick={() => nav("/dashboard")}
                  className="hidden sm:inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full hover:bg-muted"
                >
                  <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} /> {t("common.dashboard")}
                </button>
                <button
                  data-testid="logout-btn"
                  onClick={async () => { nav("/", { replace: true }); await logout(); }}
                  className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-foreground text-background btn-lift"
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.5} /> {t("common.logout")}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="login-link"
                  className={`hidden sm:inline-flex text-sm px-4 py-2 rounded-full ${onLandingHome ? "text-primary-foreground hover:bg-primary-foreground/10" : "hover:bg-muted"}`}
                >
                  {t("common.login")}
                </Link>
                <Link
                  to="/registrati"
                  data-testid="register-link"
                  className={`hidden sm:inline-flex text-sm px-5 py-2 rounded-full btn-lift ${onLandingHome ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground"}`}
                >
                  {t("common.register")}
                </Link>
              </>
            )}
            <button
              data-testid="menu-toggle"
              onClick={() => setOpen(!open)}
              className={`md:hidden w-11 h-11 rounded-full flex items-center justify-center transition-colors ${onLandingHome ? "bg-primary-foreground/15 hover:bg-primary-foreground/25 text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {/* Riga 2 — nav sezioni (solo su desktop e solo variante public) */}
        {variant === "public" && (
          <nav className={`hidden md:block border-t ${onLandingHome ? "border-primary-foreground/15 bg-primary" : "border-border/70 bg-background"}`}>
            <div className={`max-w-7xl mx-auto px-6 h-11 flex items-center justify-center gap-8 text-[13px] uppercase tracking-[0.22em] ${onLandingHome ? "text-primary-foreground/85" : "text-foreground/75"}`}>
              {navLinks.map(([to, label]) =>
                to.startsWith("/#") ? (
                  <a
                    key={to}
                    href={to}
                    data-testid={`nav-${to.replace("/#", "")}`}
                    className={`transition-colors ${onLandingHome ? "hover:text-primary-foreground" : "hover:text-primary"}`}
                  >
                    {label}
                  </a>
                ) : (
                  <Link
                    key={to}
                    to={to}
                    data-testid={`nav-${to.replace("/", "") || "home"}`}
                    className={`transition-colors ${onLandingHome ? "hover:text-primary-foreground" : "hover:text-primary"}`}
                  >
                    {label}
                  </Link>
                )
              )}
            </div>
          </nav>
        )}
      </header>

      {/* Mobile menu overlay */}
      {open && (
        <div data-testid="mobile-menu" className="md:hidden fixed inset-0 z-30 bg-background pt-24 overflow-y-auto">
          <nav className="max-w-2xl mx-auto px-8 space-y-5">
            {[["/", "Home"], ...navLinks].map(([to, label]) => (
              <Link
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                data-testid={`mm-${to.replace(/[/#]/g, "") || "home"}`}
                className="block font-serif-display text-4xl text-foreground hover:text-primary transition-colors"
              >
                {label}
              </Link>
            ))}
            {!user && (
              <div className="pt-6 border-t border-border space-y-3">
                <Link to="/login" onClick={() => setOpen(false)} className="block font-serif-display text-3xl text-primary">Accedi</Link>
                <Link to="/registrati" onClick={() => setOpen(false)} className="block font-serif-display text-3xl text-primary">Registrati</Link>
              </div>
            )}
            <div className="pt-8 text-muted-foreground text-sm border-t border-border">
              <div>+39 329 256 1466 · epicflowpilates@gmail.com</div>
              <div>Via Luigi Indelli 91A, Monopoli (BA)</div>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
